/**
 * The `breeding` facet: a pure reducer from one animal's breeding events to her reproductive state.
 * Events arrive oldest first by when they happened, so an old diagnosis entered today never beats a
 * newer one. A cycle runs from one calving (or loss) to the next. Due dates are calculated, never
 * stored on events, and always carry their basis so the screen can say how they were worked out.
 */
import { addDays, daysBetween, msg, type Badge, type Fact, type FacetEvent } from "../../../contract";
import { ageInDays, dateMsg, daysMsg, eventDay, pregnantDueMsg } from "./dates";
import { DECISIONS, STATUS_LABELS } from "./labels";
import type {
  AiData, BreedingState, CalvingData, CheckRecord, EligibilityData, ExposureData, FosterData, LossData, PregCheckData, ServiceRecord, SyncStepData, TransferData,
} from "./types";

export const DEFAULT_GESTATION = 283;
/** Days after breeding before a pregnancy can be found; services closer to a check can't explain it. */
const DETECTABLE_AFTER = 20;
/** How far a fetal-age conception date may sit from a service and still be that service. */
const MATCH_DAYS = 10;
export const FACET_VERSION = 1;

/** Fact labels are plain English keys the web translates; listed here for the localization check. */
export const FACT_LABELS = [
  "Breeding", "Earliest due (calculated)", "Latest due (calculated)", "Due (calculated)", "Recheck on", "Last calved", "Calving interval",
  "Days open", "Days since calving", "With the cows since", "Breeding decision",
];

export function gestationFrom(settings?: Record<string, unknown> | null): number {
  const v = (settings?.breeding as { gestation_days?: unknown } | undefined)?.gestation_days;
  return typeof v === "number" && Number.isFinite(v) && v >= 240 && v <= 320 ? Math.round(v) : DEFAULT_GESTATION;
}

function initial(gestation: number): BreedingState {
  return { status: "unknown", has_calved: false, calvings: 0, services: [], exposures: [], checks: [], calving_history: [], losses: [], gestation_days: gestation };
}

const later = (a: string | undefined, b: string): string => (!a || b > a ? b : a);

/** A calving or loss ends the cycle. Bulls still present carry over as access, not as a breeding. */
function closeCycle(s: BreedingState, on: string | null): void {
  s.services = [];
  s.checks = [];
  s.recheck = undefined;
  s.twins_expected = undefined;
  s.sync = undefined;
  s.exposures = s.exposures.filter((x) => !x.to).map((x) => ({ ...x, from: on && on > x.from ? on : x.from, carried: true }));
}

function addService(s: BreedingState, rec: ServiceRecord): void {
  s.services.push(rec);
  if (!s.last_service || rec.on >= s.last_service.on) s.last_service = { on: rec.on, kind: rec.kind, ...(rec.sire_id ? { sire_id: rec.sire_id } : {}) };
  s.status = "bred";
  s.sync = undefined;
}

export function reduceBreeding(events: FacetEvent[], opts: { gestation?: number } = {}): BreedingState | null {
  const G = opts.gestation ?? DEFAULT_GESTATION;
  const s = initial(G);
  let touched = false;
  for (const e of events) {
    const on = eventDay(e.occurred_at);
    const has = (role: string) => e.roles.includes(role);
    const subjectsIn = (role: string) => e.subjects.filter((x) => x.role === role).map((x) => x.id);
    switch (e.type) {
      case "breeding.heat": {
        if (!has("animal")) break;
        touched = true;
        if (on) s.last_heat = later(s.last_heat, on);
        break;
      }
      case "breeding.sync_step": {
        if (!has("animal")) break;
        touched = true;
        const d = e.data as SyncStepData;
        if (on) s.sync = { on, ...(d.protocol ? { protocol: d.protocol } : {}), ...(d.protocol_day !== undefined ? { day: d.protocol_day } : {}), ...(e.job_id ? { job_id: e.job_id } : {}) };
        break;
      }
      case "breeding.ai": {
        if (!has("animal")) break;
        touched = true;
        const d = e.data as AiData;
        if (!on || d.result === "failed") break;
        addService(s, { on, kind: "ai", event_id: e.id, ...(d.sire_id ? { sire_id: d.sire_id } : {}), ...(d.sire_name ? { sire_name: d.sire_name } : {}) });
        break;
      }
      case "breeding.embryo_transfer": {
        if (!has("recipient")) break;
        touched = true;
        const d = e.data as TransferData;
        if (!on || d.result === "not_transferred") break;
        const donor = subjectsIn("donor")[0];
        addService(s, {
          on, kind: "et", event_id: e.id, ...(d.sire_id ? { sire_id: d.sire_id } : {}), ...(donor ? { donor_id: donor } : {}),
          ...(d.embryo_age_days ? { embryo_age_days: d.embryo_age_days } : {}),
        });
        break;
      }
      case "breeding.exposure": {
        const d = e.data as ExposureData;
        const bulls = subjectsIn("bull");
        const group = subjectsIn("group")[0];
        const place = subjectsIn("place")[0];
        if (has("female")) {
          touched = true;
          if (!on) break;
          if (d.action === "in") {
            for (const bull of bulls.length ? bulls : [undefined]) {
              if (s.exposures.some((x) => !x.to && x.bull_id === bull)) continue;
              s.exposures.push({ from: on, event_id: e.id, ...(bull ? { bull_id: bull } : {}), ...(group ? { group_id: group } : {}), ...(place ? { place_id: place } : {}) });
            }
            if (s.status === "open" || s.status === "unknown" || s.status === "calved") s.status = "exposed";
          } else {
            for (const x of s.exposures) if (!x.to && (!bulls.length || (x.bull_id && bulls.includes(x.bull_id)) || !x.bull_id)) x.to = on < x.from ? x.from : on;
          }
        }
        if (has("bull")) {
          touched = true;
          if (!on) break;
          const bull = s.bull ?? { turnouts: 0 };
          if (d.action === "in") {
            s.bull = { ...bull, in_with: { since: on, females: subjectsIn("female").length, event_id: e.id, ...(group ? { group_id: group } : {}), ...(place ? { place_id: place } : {}) }, turnouts: bull.turnouts + 1 };
          } else {
            s.bull = { turnouts: bull.turnouts, last_out: on };
          }
        }
        break;
      }
      case "breeding.preg_check": {
        if (!has("animal")) break;
        touched = true;
        const d = e.data as PregCheckData;
        if (!on) break;
        const age = d.result === "pregnant" ? ageInDays(d.fetal_age) : undefined;
        const rec: CheckRecord = { on, result: d.result, event_id: e.id, ...(d.method ? { method: d.method } : {}), ...(age ? { fetal_age_days: age } : {}), ...(d.twins ? { twins: true } : {}) };
        s.checks.push(rec);
        s.last_check = rec;
        if (d.result === "pregnant") { s.status = "pregnant"; s.recheck = undefined; s.twins_expected = d.twins || undefined; }
        else if (d.result === "open") { s.status = "open"; s.recheck = undefined; s.twins_expected = undefined; }
        else s.recheck = { event_id: e.id, ...(d.recheck_on ? { on: d.recheck_on } : {}) };
        break;
      }
      case "breeding.calving": {
        if (!has("dam")) break;
        touched = true;
        const d = e.data as CalvingData;
        s.calving_history.push({
          on, precision: e.occurred_precision, event_id: e.id,
          calves: (d.calves ?? []).map((c) => ({ ...(c.calf_id ? { id: c.calf_id } : {}), ...(c.tag ? { tag: c.tag } : {}), ...(c.sex ? { sex: c.sex } : {}), outcome: c.outcome })),
          ...(d.ease ? { ease: d.ease } : {}), ...(d.sire_id ? { sire_id: d.sire_id } : {}), ...(d.sire_basis ? { sire_basis: d.sire_basis } : {}),
        });
        s.calvings += 1;
        s.has_calved = true;
        if (on) {
          if (s.last_calved && on > s.last_calved) s.calving_interval = daysBetween(s.last_calved, on);
          s.last_calved = later(s.last_calved, on);
        }
        s.status = "calved";
        closeCycle(s, on);
        break;
      }
      case "breeding.loss": {
        if (!has("animal")) break;
        touched = true;
        const d = e.data as LossData;
        if (on) s.losses.push({ on, kind: d.kind, event_id: e.id });
        s.status = "open";
        closeCycle(s, on);
        break;
      }
      case "breeding.foster": {
        const d = e.data as FosterData;
        if (d.result === "failed") { if (has("nurse") || has("calf")) touched = true; break; }
        const calf = subjectsIn("calf")[0];
        const nurse = subjectsIn("nurse")[0];
        if (has("nurse") && calf && on) {
          touched = true;
          s.nursing = [...(s.nursing ?? []).filter((n) => n.calf_id !== calf), { calf_id: calf, since: on, event_id: e.id }];
        }
        if (has("calf") && nurse && on) {
          touched = true;
          s.nurse = { nurse_id: nurse, since: on, event_id: e.id };
        }
        break;
      }
      case "breeding.eligibility": {
        if (!has("animal")) break;
        touched = true;
        const d = e.data as EligibilityData;
        if (!on) break;
        s.eligibility = {
          decision: d.decision, on, event_id: e.id, ...(d.use ? { use: d.use } : {}), ...(d.reasons?.length ? { reasons: d.reasons } : {}),
          ...(d.review_on ? { review_on: d.review_on } : {}), ...(d.bse_result ? { bse_result: d.bse_result } : {}),
        };
        break;
      }
    }
  }
  if (!touched) return null;
  // Open but still running with a bull: she can still settle this season.
  if (s.status === "open" && s.exposures.some((x) => !x.to)) s.status = "exposed";
  computeDue(s, G);
  return s;
}

function serviceConceived(x: ServiceRecord): string {
  return x.kind === "et" ? addDays(x.on, -(x.embryo_age_days ?? 7)) : x.on;
}

function setFromService(s: BreedingState, x: ServiceRecord, G: number): void {
  s.conceived_on = serviceConceived(x);
  s.due_date = addDays(s.conceived_on, G);
  s.due_basis = x.kind === "et" ? "transfer" : "service";
  if (x.kind === "et" && !x.embryo_age_days) s.due_assumed_embryo_age = true;
  if (x.sire_id) { s.likely_sire_id = x.sire_id; s.candidate_sire_ids = [x.sire_id]; }
}

/** Expected calving for a pregnant female, from the best evidence in the current cycle. */
function computeDue(s: BreedingState, G: number): void {
  if (s.status !== "pregnant") return;
  const pregnant = s.checks.filter((c) => c.result === "pregnant");
  const latest = pregnant.at(-1);
  if (!latest) return;
  const lastOpen = s.checks.filter((c) => c.result === "open" && c.on < latest.on).at(-1)?.on;
  const cutoff = addDays(latest.on, -DETECTABLE_AFTER);
  const services = s.services.filter((x) => (!lastOpen || x.on > lastOpen) && x.on <= cutoff);
  const exposures = s.exposures.filter((x) => x.from <= cutoff && (!lastOpen || !x.to || x.to > lastOpen));
  const aged = pregnant.filter((c) => (!lastOpen || c.on > lastOpen) && c.fetal_age_days);

  if (aged.length) {
    // The earliest aged check is the most precise: young pregnancies are easier to age.
    const c = aged[0];
    const conceived = addDays(c.on, -(c.fetal_age_days as number));
    const match = services
      .map((x) => ({ x, gap: Math.abs(daysBetween(serviceConceived(x), conceived)) }))
      .filter((m) => m.gap <= MATCH_DAYS)
      .sort((a, b) => a.gap - b.gap)[0];
    if (match) setFromService(s, match.x, G);
    else {
      s.conceived_on = conceived;
      s.due_date = addDays(conceived, G);
      s.due_basis = "fetal_age";
      const bulls = [...new Set(exposures
        .filter((x) => x.from <= addDays(conceived, MATCH_DAYS) && (!x.to || x.to >= addDays(conceived, -MATCH_DAYS)))
        .map((x) => x.bull_id).filter((b): b is string => !!b))];
      if (bulls.length) s.candidate_sire_ids = bulls;
      if (bulls.length === 1) s.likely_sire_id = bulls[0];
      const explained = exposures.some((x) => x.from <= addDays(conceived, MATCH_DAYS) && (!x.to || x.to >= addDays(conceived, -MATCH_DAYS)));
      if ((services.length || exposures.length) && !explained) s.conflict = "fetal_age_vs_services";
    }
  } else if (services.length === 1 && !exposures.length) {
    setFromService(s, services[0], G);
  } else if (services.length || exposures.length) {
    const starts = [...services.map(serviceConceived), ...exposures.map((x) => (lastOpen && lastOpen > x.from ? lastOpen : x.from))];
    const ends = [...services.map(serviceConceived), ...exposures.map((x) => (x.to && x.to < cutoff ? x.to : cutoff))];
    const start = starts.sort()[0];
    const end = ends.sort().at(-1)!;
    s.due_date = addDays(start, G);
    if (end > start) s.due_end = addDays(end, G);
    s.due_basis = exposures.length ? "exposure" : services.every((x) => x.kind === "et") ? "transfer" : "service";
    if (services.some((x) => x.kind === "et" && !x.embryo_age_days)) s.due_assumed_embryo_age = true;
    const sires = [...new Set([...services.map((x) => x.sire_id), ...exposures.map((x) => x.bull_id)].filter((b): b is string => !!b))];
    if (sires.length) s.candidate_sire_ids = sires;
    if (sires.length === 1 && services.length + new Set(exposures.map((x) => x.bull_id)).size <= 1) s.likely_sire_id = sires[0];
  }
  if (s.conceived_on && s.last_calved && s.conceived_on > s.last_calved) s.days_open = daysBetween(s.last_calved, s.conceived_on);
}

// ---------------------------------------------------------------- badges and facts

/** Days until the due date (negative when past). */
export function daysToDue(s: Pick<BreedingState, "due_date">, today: string): number | null {
  return s.due_date ? daysBetween(today, s.due_date) : null;
}

export function breedingBadges(s: BreedingState, today: string): Badge[] {
  const out: Badge[] = [];
  const days = daysToDue(s, today);
  if (s.status === "pregnant" && days !== null) {
    if (days < 0) out.push({ label: days === -1 ? msg("Past due 1 day") : msg("Past due {{count}} days", { count: -days }), tone: "warn", icon: "calf" });
    else if (days <= 7) out.push({ label: msg("Due this week"), tone: "warn", icon: "calf" });
  }
  if (s.status === "pregnant") out.push({ label: s.due_date ? pregnantDueMsg(s.due_date) : msg("Pregnant"), tone: "repro", icon: "calf" });
  else if (s.status === "bred") out.push({ label: msg("Bred"), tone: "repro", icon: "heart" });
  else if (s.status === "open") out.push({ label: s.last_check?.result === "open" && (!s.losses.length || s.last_check.on > s.losses.at(-1)!.on) ? msg("Checked open") : msg("Lost pregnancy"), tone: "warn", icon: "heart" });
  if (s.recheck) out.push({ label: msg("Recheck"), tone: "warn", icon: "vet" });
  if (s.bull?.in_with) out.push({ label: msg("With the cows"), tone: "repro", icon: "fence" });
  if (s.nurse) out.push({ label: msg("On a nurse cow"), tone: "repro", icon: "heart" });
  const decision = s.eligibility?.decision;
  if (decision === "cull" || decision === "sell_open" || decision === "do_not_breed") out.push({ label: msg(DECISIONS[decision]), tone: "bad", icon: "warn" });
  return out;
}

export function breedingFacts(s: BreedingState, today: string): Fact[] {
  const out: Fact[] = [];
  const female = s.status !== "unknown" || s.has_calved || s.services.length > 0 || s.checks.length > 0;
  if (female) out.push({ label: "Breeding", value: msg(STATUS_LABELS[s.status]), tone: "repro", icon: "heart" });
  if (s.status === "pregnant" && s.due_date) {
    if (s.due_end) {
      out.push({ label: "Earliest due (calculated)", value: dateMsg(s.due_date), tone: "repro", icon: "calf" });
      out.push({ label: "Latest due (calculated)", value: dateMsg(s.due_end), tone: "repro", icon: "calf" });
    } else out.push({ label: "Due (calculated)", value: dateMsg(s.due_date), tone: "repro", icon: "calf" });
  }
  if (s.recheck?.on) out.push({ label: "Recheck on", value: dateMsg(s.recheck.on), tone: "warn", icon: "vet" });
  if (s.last_calved) out.push({ label: "Last calved", value: dateMsg(s.last_calved), icon: "calf" });
  if (s.calving_interval) out.push({ label: "Calving interval", value: daysMsg(s.calving_interval) });
  if (s.days_open !== undefined) out.push({ label: "Days open", value: daysMsg(s.days_open) });
  else if (s.last_calved && s.status !== "pregnant") {
    const open = daysBetween(s.last_calved, today);
    if (open >= 0) out.push({ label: "Days since calving", value: daysMsg(open) });
  }
  if (s.bull?.in_with) out.push({ label: "With the cows since", value: dateMsg(s.bull.in_with.since), icon: "fence" });
  if (s.eligibility) out.push({ label: "Breeding decision", value: msg(DECISIONS[s.eligibility.decision]), tone: s.eligibility.decision === "keep" ? "good" : s.eligibility.decision === "defer" ? "warn" : "bad" });
  return out;
}
